import '@fontsource-variable/inter';

// === COLORS ===

/** Design System colors */
export const COLORS = {
  red: {
    50: '#FFE5E5',
    100: '#FFCCCC',
    200: '#FF7373',
    300: '#F55656',
    400: '#DB3737',
    500: '#C23030',
    600: '#A82A2A',
    700: '#9B2C2C',
    800: '#822727',
    900: '#63171B',
  },
  orange: {
    50: '#FFF2E5',
    75: '#F7E0B4',
    100: '#FFE1C2',
    200: '#FFB366',
    300: '#F29D49',
    400: '#D9822B',
    500: '#BF7326',
    600: '#A66321',
    700: '#9C4221',
    800: '#7B341E',
    900: '#652B19',
  },
  green: {
    50: '#E6FEF4',
    100: '#BDFAE1',
    200: '#3DCC91',
    300: '#15B371',
    400: '#0F9960',
    500: '#0D8050',
    600: '#0A6640',
    700: '#276749',
    800: '#22543D',
    900: '#1C4532',
  },
  blue: {
    50: '#E6F5FE',
    100: '#BDE2FA',
    200: '#48AFF0',
    300: '#2B95D6',
    400: '#137CBD',
    500: '#106BA3',
    600: '#0E5A8A',
    700: '#2c5282',
    800: '#2a4365',
    900: '#1A365D',
  },
  gray: {
    20: '#FAFAFA',
    50: '#F2F2F2',
    100: '#E6E6E6',
    200: '#D1D1D1',
    300: '#B8B8B8',
    400: '#9E9E9E',
    500: '#757575',
    600: '#666666',
    700: '#4D4D4D',
    800: '#333333',
    900: '#1A1A1A',
    950: '#0F0F0F',
  },
  'v2-aurora': {
    50: '#E6ECED',
    100: '#CCDADB',
    200: '#97B2B5',
    300: '#678F92',
    400: '#2D6469',
    500: '#02444A',
    600: '#02363B',
    700: '#012E32',
    800: '#011B1E',
    900: '#000E0F',
  },
} as const;

/** Brand colors, basically the logo */
export const COLORS_BRAND = {
  poppy: {
    50: '#FDF7ED',
    300: '#F4C671', // brand guide Poppy 30
    400: '#EBB347', // brand guide Poppy 40
    500: '#D99C26', // brand guide Poppy 50
  },
  sage: {
    50: '#F8FAF6', // brand guide Sage 02
    100: '#DFE4D5', // brand guide Sage 10
  },
};

/** Chart colors */
export const COLORS_CHARTS = {
  blush: '#D38F7A',
  celadon: '#569D6D',
  coral: '#D87552',
  cream: '#D2B874',
  dustyRose: '#C97E90',
  jasmine: '#93B76A',
  lavender: '#A87CC0',
  lilac: '#8C7BCB',
  mint: '#66B8A3',
  paleViolet: '#9C5FC0',
  peach: '#D9905C',
  pink: '#D15CA8',
  sky: '#5CA9D8',
  slateBlue: '#4A6C94',
  teal: '#50A6B4',
  winter: '#60B7D8',
  yellow: '#D5A03E',
} as const;

/** Extended color scheme, only use for color-dense content like graphs and pie charts */
export const COLORS_EXTENDED = {
  vermillion: {
    100: '#FFB7A5',
    200: '#F4A691',
    300: '#E9947D',
    400: '#DD836A',
    500: '#D17257',
    600: '#C56145',
    700: '#B85033',
    800: '#AB3E21',
    900: '#9E2B0E',
  },
  rose: {
    100: '#FFB3D0',
    200: '#F5A2C0',
    300: '#EB91AF',
    400: '#E080A0',
    500: '#D56F90',
    600: '#CA5D81',
    700: '#BF4B72',
    800: '#B43863',
    900: '#A82255',
  },
  brick: {
    50: '#FEF8F8',
    100: '#FDF0F0',
    150: '#FDE9E9',
    200: '#FCE1E1',
    300: '#FAD2D2',
    400: '#F9C3C3',
    500: '#FF9B9B',
    600: '#E57373',
    700: '#C25B5B',
    800: '#A54242',
    850: '#9D3F3F',
    900: '#912E2E',
    950: '#863030',
  },
  violet: {
    100: '#E1BAE1',
    200: '#D0A6CF',
    300: '#BF93BE',
    400: '#AE80AD',
    500: '#9D6D9C',
    600: '#8C5B8C',
    700: '#7C497B',
    800: '#6C376B',
    900: '#5C255C',
  },
  indigo: {
    100: '#D6CCFF',
    200: '#C6BAF4',
    300: '#B7A8E8',
    400: '#A796DD',
    500: '#9784D2',
    600: '#8773C7',
    700: '#7763BC',
    800: '#6752B1',
    900: '#5642A6',
  },
  cobalt: {
    100: '#B3CFFF',
    200: '#A2BDF2',
    300: '#91ACE5',
    400: '#809BD8',
    500: '#6F8ACB',
    600: '#5E79BE',
    700: '#4B6AB2',
    800: '#385AA5',
    900: '#1F4B99',
  },
  turquoise: {
    100: '#87E4DB',
    200: '#78D5CC',
    300: '#68C6BD',
    400: '#58B8AE',
    500: '#47AA9F',
    600: '#369C91',
    700: '#369C91',
    800: '#228E83',
    900: '#008075',
  },
  forest: {
    100: '#B1ECB5',
    200: '#9FDCA2',
    300: '#8DCD8F',
    400: '#7CBD7D',
    500: '#6AAE6A',
    600: '#589F59',
    700: '#469047',
    800: '#338136',
    900: '#1D7324',
  },
  lime: {
    100: '#E8F9B6',
    200: '#D9EBA3',
    300: '#CADD91',
    400: '#BBCF7F',
    500: '#ADC16D',
    600: '#9EB45B',
    700: '#8FA649',
    800: '#819937',
    900: '#728C23',
  },
  gold: {
    100: '#FFE4A0',
    200: '#F4D68E',
    300: '#EAC87D',
    400: '#DFBA6B',
    500: '#D4AD5A',
    600: '#C8A048',
    700: '#BD9336',
    800: '#B28623',
    900: '#A67908',
  },
  sepia: {
    100: '#E4CBB2',
    200: '#D4B89E',
    300: '#C3A68A',
    400: '#B39477',
    500: '#A38364',
    600: '#937252',
    700: '#836140',
    800: '#73512F',
    900: '#63411E',
  },
} as const;

// === COLOR SETS ===

/**
 * A "color set" is a set of closely related, sequential color values, usually of a single color.
 * For example, a red button can have a background of `red.400` to start, then darken to `red.500`
 * on hover, then to `red.600` on mouse press, and so on.
 */
export const COLOR_SETS = {
  /** Stark, prominent colors, use to make UI stand out, like with primary CTA buttons */
  prominent: [
    { light: '{colorScheme}.400', dark: '{colorScheme}.300' },
    { light: '{colorScheme}.500', dark: '{colorScheme}.200' },
    { light: '{colorScheme}.600', dark: '{colorScheme}.100' },
    { light: '{colorScheme}.700', dark: '{colorScheme}.50' },
  ],
  prominentText: [
    { light: '{colorScheme}.800', dark: '{colorScheme}.100' },
    { light: '{colorScheme}.900', dark: '{colorScheme}.50' },
    { light: '{colorScheme}.950', dark: 'white' },
    { light: '{colorScheme}.950', dark: 'white' },
  ],

  /** Lightest/darkest color value we can use for non-layout backgrounds */
  muted: [
    { light: '{colorScheme}.50', dark: '{colorScheme}.800' },
    { light: '{colorScheme}.100', dark: '{colorScheme}.700' },
    { light: '{colorScheme}.200', dark: '{colorScheme}.600' },
    { light: '{colorScheme}.300', dark: '{colorScheme}.500' },
  ],
  mutedText: [
    { light: '{colorScheme}.500', dark: '{colorScheme}.300' },
    { light: '{colorScheme}.600', dark: '{colorScheme}.200' },
    { light: '{colorScheme}.700', dark: '{colorScheme}.100' },
    { light: '{colorScheme}.800', dark: '{colorScheme}.50' },
  ],

  /** Bright, or sort of "middle-ground" color, used for colorful borders, maybe chart legends? */
  bright: [
    { light: '{colorScheme}.200', dark: '{colorScheme}.600' },
    { light: '{colorScheme}.300', dark: '{colorScheme}.500' },
    { light: '{colorScheme}.400', dark: '{colorScheme}.300' },
    { light: '{colorScheme}.500', dark: '{colorScheme}.200' },
  ],

  /** Transparent background, except when hovered */
  ghost: [
    { light: 'transparent', dark: 'transparent' },
    { light: '{colorScheme}.50', dark: '{colorScheme}.800' },
    { light: '{colorScheme}.100', dark: '{colorScheme}.700' },
    { light: '{colorScheme}.200', dark: '{colorScheme}.600' },
  ],

  /** Transparent background */
  transparent: [
    { light: 'transparent', dark: 'transparent' },
    { light: 'transparent', dark: 'transparent' },
    { light: 'transparent', dark: 'transparent' },
    { light: 'transparent', dark: 'transparent' },
  ],

  /** White and black (flips based on colorMode) */
  white: [
    { light: 'white', dark: 'gray.950' },
    { light: 'gray.50', dark: 'gray.900' },
    { light: 'gray.100', dark: 'gray.800' },
    { light: 'gray.200', dark: 'gray.700' },
  ],
  black: [
    { light: 'gray.950', dark: 'white' },
    { light: 'gray.900', dark: 'gray.50' },
    { light: 'gray.800', dark: 'gray.100' },
    { light: 'gray.700', dark: 'gray.200' },
  ],
} as const;

// === TYPOGRAPHY ===

export const FONT_FAMILY = {
  // sans-serif
  base: [
    // Preferred Finley font
    'Inter Variable',
    // Mac system fonts
    '-apple-system',
    'BlinkMacSystemFont',
    // Windows system fonts
    'Segoe UI',
    // Common fallback fonts
    'Helvetica',
    'Arial',
    // All else failed, hopefully user has at least one sans-serif font installed in their OS
    'sans-serif',
    // Emoji fonts
    'Apple Color Emoji',
    'Segoe UI Emoji',
    'Segoe UI Symbol',
  ],
  // monospace
  // serif
} as const;

export const FONT_WEIGHTS = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;
export type FontWeights = keyof typeof FONT_WEIGHTS;

export const FONT_SIZES = {
  '3xs': '0.45rem', // chakra fontSizes-3xs
  '2xs': '0.625rem', // chakra fontSizes-2xs
  xs: '0.75rem', // chakra fontSizes-xs lineHeight-3
  sm: '0.875rem', // chakra fontSizes-sm
  md: '1rem', // chakra fontSizes-md lineHeight-4
  lg: '1.125rem', // chakra fontSizes-lg
  xl: '1.25rem', // chakra fontSizes-xl lineHeight-5
  '1.5xl': '1.375rem',
  '2xl': '1.5rem', // chakra fontSizes-2xl lineHeight-6
  '2.3xl': '1.625rem', // chakra lineHeight-7
  '2.7xl': '1.75rem', // chakra lineHeight-7
  '3xl': '1.875rem', // chakra fontSizes-3xl lineHeight-7
  '3.5xl': '2rem', // chakra lineHeight-8
  '4xl': '2.25rem', // chakra fontSizes-4xl lineHeight-9
  '4.5xl': '2.5rem', // chakra lineHeight-10
  '5xl': '3rem', // chakra fontSizes-5xl
  '5.7xl': '3.5rem',
  '6xl': '3.75rem', // chakra fontSizes-6xl
  '6.3xl': '4.0rem',
  '7xl': '4.5rem', // chakra fontSizes-7xl
  '8xl': '6rem', // chakra fontSizes-8xl
  '9xl': '8rem', // chakra fontSizes-9xl
} as const;
export type FontSizes = keyof typeof FONT_SIZES;

export const TEXT_STYLES = {
  xs: { fontSize: FONT_SIZES.xs, lineHeight: FONT_SIZES.lg },
  sm: { fontSize: FONT_SIZES.sm, lineHeight: FONT_SIZES['1.5xl'] },
  md: { fontSize: FONT_SIZES.md, lineHeight: FONT_SIZES['2xl'] },
  lg: { fontSize: FONT_SIZES.lg, lineHeight: FONT_SIZES['2xl'] },
  xl: { fontSize: FONT_SIZES.xl, lineHeight: FONT_SIZES['2.3xl'] },
  '2xl': { fontSize: FONT_SIZES['2xl'], lineHeight: FONT_SIZES['3.5xl'] },
  '3xl': { fontSize: FONT_SIZES['3xl'], lineHeight: FONT_SIZES['4xl'] },
  '4xl': { fontSize: FONT_SIZES['4xl'], lineHeight: FONT_SIZES['4.5xl'] },
  '5xl': { fontSize: FONT_SIZES['5xl'], lineHeight: FONT_SIZES['5.7xl'] },
} as const;

/** Maps "color set" tokens to common text variants. */
export const TEXT_COLORS = {
  prominent: {
    base: COLOR_SETS.prominentText[0],
    hover: COLOR_SETS.prominentText[1],
    active: COLOR_SETS.prominentText[2],
    disabled: COLOR_SETS.prominentText[0],
  },
  muted: {
    base: COLOR_SETS.mutedText[0],
    hover: COLOR_SETS.mutedText[1],
    active: COLOR_SETS.mutedText[2],
    disabled: COLOR_SETS.mutedText[0],
  },
  white: {
    base: COLOR_SETS.white[0],
    hover: COLOR_SETS.white[1],
    active: COLOR_SETS.white[2],
    disabled: COLOR_SETS.white[0],
  },
  black: {
    base: COLOR_SETS.black[0],
    hover: COLOR_SETS.black[1],
    active: COLOR_SETS.black[2],
    disabled: COLOR_SETS.black[0],
  },
} as const;

// === BACKGROUNDS ===

/** Maps "color set" tokens to common background surface variants. */
export const BACKGROUND_COLORS = {
  prominent: {
    base: COLOR_SETS.prominent[0],
    hover: COLOR_SETS.prominent[1],
    active: COLOR_SETS.prominent[2],
    disabled: COLOR_SETS.prominent[0],
  },
  muted: {
    base: COLOR_SETS.muted[0],
    hover: COLOR_SETS.muted[1],
    active: COLOR_SETS.muted[2],
    disabled: COLOR_SETS.muted[0],
  },
  ghost: {
    base: COLOR_SETS.ghost[0],
    hover: COLOR_SETS.ghost[1],
    active: COLOR_SETS.ghost[2],
    disabled: COLOR_SETS.ghost[0],
  },

  /** Transparent border, useful when animating from transparent to color borders */
  transparent: {
    base: COLOR_SETS.transparent[0],
    hover: COLOR_SETS.transparent[1],
    active: COLOR_SETS.transparent[2],
    disabled: COLOR_SETS.transparent[3],
  },

  white: {
    base: COLOR_SETS.white[0],
    hover: COLOR_SETS.white[1],
    active: COLOR_SETS.white[2],
    disabled: COLOR_SETS.white[3],
  },

  black: {
    base: COLOR_SETS.black[0],
    hover: COLOR_SETS.black[1],
    active: COLOR_SETS.black[2],
    disabled: COLOR_SETS.black[3],
  },
} as const;

// === BORDERS ===

export const BORDER_STYLES = {
  thick: { borderWidth: '2px', borderStyle: 'solid' },
  thin: { borderWidth: '1px', borderStyle: 'solid' },
} as const;

/** Maps "color set" tokens to common border colors. */
export const BORDER_COLORS = {
  prominent: {
    base: COLOR_SETS.prominent[0],
    hover: COLOR_SETS.prominent[1],
    active: COLOR_SETS.prominent[2],
    disabled: COLOR_SETS.prominent[0],
  },
  muted: {
    base: COLOR_SETS.muted[0],
    hover: COLOR_SETS.muted[1],
    active: COLOR_SETS.muted[2],
    disabled: COLOR_SETS.muted[0],
  },
  bright: {
    base: COLOR_SETS.bright[0],
    hover: COLOR_SETS.bright[1],
    active: COLOR_SETS.bright[2],
    disabled: COLOR_SETS.bright[0],
  },

  /** Transparent border, useful when animating from transparent to color borders */
  transparent: {
    base: COLOR_SETS.transparent[0],
    hover: COLOR_SETS.transparent[1],
    active: COLOR_SETS.transparent[2],
    disabled: COLOR_SETS.transparent[3],
  },
  white: {
    base: COLOR_SETS.white[1],
    hover: COLOR_SETS.white[2],
    active: COLOR_SETS.white[3],
    disabled: COLOR_SETS.white[1],
  },
  black: {
    base: COLOR_SETS.black[1],
    hover: COLOR_SETS.black[2],
    active: COLOR_SETS.black[3],
    disabled: COLOR_SETS.black[1],
  },
} as const;

/** These are Chakra border radii defaults */
export const BORDER_RADII = {
  none: '0',
  sm: '0.125rem',
  base: '0.25rem',
  md: '0.375rem',
  lg: '0.5rem',
  xl: '0.75rem',
  '2xl': '1rem',
  '2.5xl': '1.25rem',
  '3xl': '1.5rem',
  '3.5xl': '1.75rem',
  '4xl': '2rem',
  full: '9999px',
} as const;

// === SPACING ===

/** These are Chakra spacing defaults */
export const SPACING = {
  '0': '0rem',
  '0.5': '0.125rem',
  '1': '0.25rem',
  '1.5': '0.375rem',
  '2': '0.5rem',
  '2.5': '0.625rem',
  '3': '0.75rem',
  '3.5': '0.875rem',
  '4': '1rem',
  '5': '1.25rem',
  '6': '1.5rem',
  '7': '1.75rem',
  '8': '2rem',
  '9': '2.25rem',
  '10': '2.5rem',
  '12': '3rem',
  '14': '3.5rem',
  '16': '4rem',
  '20': '5rem',
  '24': '6rem',
  '28': '7rem',
  '32': '8rem',
  '36': '9rem',
  '40': '10rem',
  '44': '11rem',
  '48': '12rem',
  '52': '13rem',
  '56': '14rem',
  '60': '15rem',
  '64': '16rem',
  '72': '18rem',
  '80': '20rem',
  '96': '24rem',
  px: '1px',
} as const;

// === MISC ===

export const DISABLED_STYLES = {
  opacity: 0.5,
} as const;
